import React, { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle, Calendar, ChevronDown, ChevronRight, PlayCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatNumber } from "@/lib/utils";
import { DateGroupedTable, extractDateKey } from "@/components/shared/DateGroupedTable";
import { DeadlineText } from "@/components/shared/StackedCell";
import { deadlineStatus } from "@/lib/deadline-status";
import type { StageMetric } from "./StageProgressCell";
import { resolveStageMetric, type ProcessGroupConfig } from "./ProductionOrderMatrixGrid";

/**
 * Tiến độ công đoạn (30/10/2026): mỗi lệnh đang sản xuất là 1 dòng, mỗi nhóm công đoạn là 1 cột
 * (đạt / kế hoạch, ngày, trễ), gom theo ngày hạn giao. Khác danh sách lệnh SX: chỉ lệnh còn đang chạy,
 * đánh dấu công đoạn lệnh đang nằm, và đầu cột đếm số lệnh đang chạy / trễ / đang chờ ở công đoạn đó.
 */
const FINISHED_STATUSES = new Set(["COMPLETED", "CANCELLED", "CLOSED", "DONE"]);

const isStageActive = (s?: StageMetric) => Boolean(s && s.status !== "NA");
const isStageDone = (s?: StageMetric) => Boolean(s && (s.status === "COMPLETED" || (s.percent || 0) >= 100));
const isStageLate = (s?: StageMetric) =>
  Boolean(s && !isStageDone(s) && (s.status === "LATE" || s.isLate || /trễ/i.test(s.dueText || "")));

function customerOf(order: any): string {
  return (
    order.customerName ||
    order.salesOrder?.customer?.shortName ||
    order.salesOrder?.customer?.name ||
    order.product?.customer?.name ||
    ""
  );
}

interface Row {
  order: any;
  stages: Record<string, StageMetric | undefined>;
  currentCode: string | null;
  lateCount: number;
}

function StageCell({ stage, isCurrent }: { stage?: StageMetric; isCurrent: boolean }) {
  if (!isStageActive(stage)) {
    return <span className="text-slate-300 font-mono text-xs">—</span>;
  }
  const s = stage as StageMetric;
  const done = isStageDone(s);
  const late = isStageLate(s);
  const plan = Number(s.planQty || s.totalQty || 0);
  const got = Number(s.completedQty || 0);
  const pct = Math.max(0, Math.min(100, Math.round(s.percent || (done ? 100 : 0))));
  const unit = s.unit === "sheet" ? "tờ" : s.unit === "pcs" || !s.unit ? "sp" : s.unit;
  const when = done ? "Đã xong" : s.formattedDeadline ? `${s.formattedDeadline}${s.dueText ? ` (${s.dueText})` : ""}` : "Chưa xếp lịch";
  return (
    <div
      className={cn(
        "rounded px-1.5 py-1 leading-tight",
        isCurrent && "ring-2 ring-indigo-400",
        done ? "bg-emerald-50" : late ? "bg-rose-50" : s.status === "IN_PROGRESS" ? "bg-amber-50" : "bg-white",
      )}
      data-testid="progress-stage-cell"
      data-stage-state={done ? "done" : late ? "late" : s.status === "IN_PROGRESS" ? "running" : "waiting"}
      data-current={isCurrent ? "true" : undefined}
      title={s.detailNames?.length ? s.detailNames.join(", ") : s.groupName}
    >
      <div className="flex items-baseline justify-between gap-1 text-[11px]">
        <span className="font-mono font-bold text-slate-900 tabular-nums">
          {formatNumber(got)}/{formatNumber(plan)}
        </span>
        <span className={cn("font-bold tabular-nums", done ? "text-emerald-700" : late ? "text-rose-600" : "text-slate-500")}>{pct}%</span>
      </div>
      <div className="h-1 mt-0.5 rounded bg-slate-200 overflow-hidden">
        <div className={cn("h-full", done ? "bg-emerald-500" : late ? "bg-rose-500" : "bg-indigo-500")} style={{ width: `${pct}%` }} />
      </div>
      <div className={cn("mt-0.5 text-[10px] truncate", done ? "text-emerald-700" : late ? "text-rose-600 font-semibold" : "text-slate-500")}>
        {late && !done ? "Trễ · " : ""}
        {when}
        <span className="text-slate-400"> · {unit}</span>
      </div>
    </div>
  );
}

export function ProcessProgressBoard({
  orders,
  activeProcessGroups,
  isLoading,
}: {
  orders: any[];
  activeProcessGroups: ProcessGroupConfig[];
  isLoading?: boolean;
}) {
  const navigate = useNavigate();

  const rows: Row[] = useMemo(
    () =>
      orders
        .filter((o) => !FINISHED_STATUSES.has(String(o.status || "").toUpperCase()))
        .map((order) => {
          const metrics = order.processGroupMetrics || {};
          const stages: Record<string, StageMetric | undefined> = {};
          for (const g of activeProcessGroups) stages[g.code] = resolveStageMetric(metrics, g.code);
          const current = activeProcessGroups.find((g) => isStageActive(stages[g.code]) && !isStageDone(stages[g.code]));
          const lateCount = activeProcessGroups.filter((g) => isStageLate(stages[g.code])).length;
          return { order, stages, currentCode: current?.code || null, lateCount };
        }),
    [orders, activeProcessGroups],
  );

  // Đầu cột: số lệnh đang chạy / trễ / đang nằm chờ ở công đoạn
  const columnCounts = useMemo(() => {
    const out: Record<string, { running: number; late: number; current: number }> = {};
    for (const g of activeProcessGroups) {
      out[g.code] = { running: 0, late: 0, current: 0 };
      for (const r of rows) {
        const s = r.stages[g.code];
        if (s?.status === "IN_PROGRESS" && !isStageDone(s)) out[g.code].running++;
        if (isStageLate(s)) out[g.code].late++;
        if (r.currentCode === g.code) out[g.code].current++;
      }
    }
    return out;
  }, [rows, activeProcessGroups]);

  const colCount = 3 + activeProcessGroups.length;

  return (
    <div className="h-full overflow-auto" data-testid="process-progress-board">
      <table className="apv-compact-table w-full text-xs border-collapse min-w-max">
        <thead>
          <tr>
            <th className="sticky left-0 z-30 min-w-[230px] text-left px-3 py-2">Lệnh SX · Sản phẩm</th>
            <th className="min-w-[130px] text-left px-2 py-2">Hạn giao</th>
            <th className="min-w-[110px] text-left px-2 py-2">Đang ở</th>
            {activeProcessGroups.map((g) => {
              const c = columnCounts[g.code];
              return (
                <th key={g.code} className="min-w-[132px] text-left px-2 py-2 align-top" data-testid="progress-stage-header">
                  <div className="font-semibold">{g.name}</div>
                  <div className="mt-0.5 flex items-center gap-2 text-[10px] font-normal normal-case">
                    <span className="text-indigo-700" title="Số lệnh đang nằm ở công đoạn này">{c?.current || 0} chờ</span>
                    <span className="text-amber-700 flex items-center gap-0.5" title="Đang chạy">
                      <PlayCircle className="w-3 h-3" />
                      {c?.running || 0}
                    </span>
                    <span className={cn("flex items-center gap-0.5", c?.late ? "text-rose-600 font-bold" : "text-slate-400")} title="Trễ hạn">
                      <AlertTriangle className="w-3 h-3" />
                      {c?.late || 0}
                    </span>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <tr key={i}>
                {Array.from({ length: colCount }).map((__, j) => (
                  <td key={j} className="px-2 py-2">
                    <Skeleton className="h-3 w-16" />
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <DateGroupedTable<Row>
              items={rows}
              getDateKey={(r) => extractDateKey(r.order.deadline)}
              sortOrder="asc"
              emptyState={
                <tr>
                  <td colSpan={colCount} className="text-center text-slate-400 py-12 text-sm">
                    Không có lệnh sản xuất đang chạy
                  </td>
                </tr>
              }
              renderGroupHeader={(group) => {
                const late = group.items.filter((r) => r.lateCount > 0).length;
                return (
                  <tr
                    key={`group-${group.dateKey}`}
                    data-testid="progress-date-group"
                    className={cn(
                      "border-y font-semibold text-xs",
                      group.isToday ? "bg-[#eef2ff] border-indigo-200" : "bg-[#f1f5f9] border-slate-300",
                    )}
                  >
                    <td colSpan={colCount} className="py-2 px-3">
                      <div className="sticky left-3 flex items-center gap-2 w-max">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 p-0 text-slate-600"
                          onClick={group.toggleCollapse}
                          title={group.isCollapsed ? "Bung ra" : "Thu gọn"}
                        >
                          {group.isCollapsed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                        </Button>
                        <span className="font-bold text-slate-800 text-[13px] flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-indigo-600" />
                          Hạn giao {group.formattedDate}
                        </span>
                        <Badge variant="outline" className="bg-white/80 text-slate-700 border-slate-300 text-[11px] font-bold px-2 py-0">
                          {group.count} lệnh
                        </Badge>
                        {late > 0 && (
                          <Badge className="bg-rose-600 text-white text-[10px] font-bold px-1.5 py-0 border-0">{late} lệnh có công đoạn trễ</Badge>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              }}
              renderRow={(r) => {
                const o = r.order;
                const code = o.orderId || o.lsxId || o.id;
                const current = activeProcessGroups.find((g) => g.code === r.currentCode);
                const due = deadlineStatus(o.deadline, {
                  done: String(o.status || "").toUpperCase() === "COMPLETED",
                  overdueLabel: (d) => `Trễ ${d} ngày`,
                });
                return (
                  <tr
                    key={o.id || code}
                    data-testid="progress-row"
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() =>
                      navigate({ to: "/production-orders/$orderId", params: { orderId: code } as any, search: { tab: "operations" } as any })
                    }
                  >
                    <td className="sticky left-0 z-10 bg-white px-3 py-1.5 border-r border-slate-200">
                      <div className="font-mono font-bold text-emerald-700 text-[12px]">{code}</div>
                      <div className="text-[11px] text-slate-800 truncate max-w-[220px]" title={o.productName}>{o.productName}</div>
                      <div className="text-[10px] text-slate-500 truncate max-w-[220px]">{customerOf(o)}</div>
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="text-[12px] text-slate-900">{o.deadline ? new Date(o.deadline).toLocaleDateString("vi-VN") : "—"}</div>
                      <div className="text-[11px]">
                        <DeadlineText status={due} testId="progress-row-due" />
                      </div>
                    </td>
                    <td className="px-2 py-1.5" data-testid="progress-row-current">
                      {current ? (
                        <span className="text-[11px] font-semibold text-indigo-700">{current.name}</span>
                      ) : (
                        <span className="text-[11px] text-emerald-700 font-semibold">Xong công đoạn</span>
                      )}
                      {r.lateCount > 0 && <div className="text-[10px] text-rose-600 font-semibold">{r.lateCount} công đoạn trễ</div>}
                    </td>
                    {activeProcessGroups.map((g) => (
                      <td key={g.code} className="px-1.5 py-1 align-middle">
                        <StageCell stage={r.stages[g.code]} isCurrent={r.currentCode === g.code} />
                      </td>
                    ))}
                  </tr>
                );
              }}
            />
          )}
        </tbody>
      </table>
    </div>
  );
}
